import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, useFormState } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { DateTime } from 'luxon';
import { BottomSheet, Tabs } from '@utriper/core-ui';
import { Box, Button, Grid2, Popover, Typography, useMediaQuery } from '@mui/material';
import { validateUnitCapacity } from '@utriper/core';
import { FREQUENCY, UNTIL_TYPE } from './utils';
import WhenTab from './components/WhenTab';
import CapacityTab from './components/CapacityTab';
import PriceTab from './components/PriceTab';
import RepeatTab from './components/RepeatTab';
import { SUBMIT_TYPE } from '../../hook';

const TAB_IDS = {
    WHEN: 0,
    CAPACITY: 1,
    PRICE: 2,
    REPEAT: 3,
};

const FormContent = ({ form, ui }) => {
    const { formApi, handleSubmit = () => {}, handleClose = () => {}, isEdit = false, loading } = form;
    const { activeTab, setActiveTab, isMobile, translate } = ui;
    const { errors } = useFormState();

    const tabOptions = [
        {
            id: TAB_IDS.WHEN,
            label: translate('tabs.when_tab'),
        },
        {
            id: TAB_IDS.CAPACITY,
            label: translate('tabs.capacity_tab'),
        },
        {
            id: TAB_IDS.PRICE,
            label: translate('tabs.price_tab'),
        },
        {
            id: TAB_IDS.REPEAT,
            label: translate('tabs.repeat_tab'),
        },
    ];

    const handleNext = (e) => {
        if (errors && Object.keys(errors)?.length > 0) {
            return;
        }

        if (activeTab === TAB_IDS.REPEAT || isEdit) {
            return handleSubmit(e);
        }

        setActiveTab((prevTab) => prevTab + 1);
        return;
    };

    const handleDelete = (e) => {
        formApi.change('isDelete', true);

        return handleSubmit(e);
    };

    return (
        <Box p={2}>
            {!isMobile && (
                <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                    <Typography fontSize={20} fontWeight={900} sx={{ color: '#131313' }} noWrap>
                        {isEdit ? translate('title.update') : translate('title.add')}
                    </Typography>
                </Grid2>
            )}

            <Tabs
                value={activeTab}
                options={tabOptions}
                onChange={(newTab) => setActiveTab(newTab)}
                variant="scrollable"
                scrollButtons={false}
                sx={{
                    '& .MuiTabs-indicator': { backgroundColor: '#131313' },
                    '& .MuiButtonBase-root': { color: '#131313 !important', textTransform: 'none' },
                }}
            />

            <Box
                sx={{
                    pt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                }}
            >
                {activeTab === TAB_IDS.WHEN && <WhenTab />}
                {activeTab === TAB_IDS.CAPACITY && <CapacityTab form={formApi} />}
                {activeTab === TAB_IDS.PRICE && <PriceTab form={formApi} />}
                {activeTab === TAB_IDS.REPEAT && <RepeatTab form={formApi} isMobile={isMobile} />}
            </Box>

            <Box
                sx={{
                    pt: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '8px',
                }}
            >
                <Button
                    variant="outlined"
                    sx={{
                        color: '#131313',
                        borderRadius: '8px',
                        border: 'solid 1px #13131340',
                        textTransform: 'none',
                    }}
                    disabled={loading}
                    onClick={(e) => {
                        handleClose();
                        formApi.reset(e);
                    }}
                >
                    {translate('buttons.cancel')}
                </Button>

                {isEdit && (
                    <Button
                        variant="contained"
                        sx={{
                            color: '#fff',
                            backgroundColor: '#C51313',
                            borderRadius: '8px',
                            textTransform: 'none',
                        }}
                        disabled={loading}
                        onClick={handleDelete}
                    >
                        {translate('buttons.delete_event')}
                    </Button>
                )}

                <Button
                    type="submit"
                    variant="contained"
                    sx={{
                        color: '#fff',
                        backgroundColor: '#137DC5',
                        borderRadius: '8px',
                        textTransform: 'none',
                    }}
                    disabled={loading}
                    onClick={handleNext}
                >
                    {activeTab === TAB_IDS.REPEAT && !isEdit ? translate('buttons.add_event') : isEdit ? translate('buttons.update_event') : translate('buttons.next')}
                </Button>
            </Box>
        </Box>
    );
};

const ScheduleForm = ({ data = {}, actions = {}, config = {} }) => {
    const { initialValues, start, end, timezone, loading } = data;
    const { onClose = () => {}, onSubmit = () => {} } = actions;
    const {
        open = false,
        isEdit = false,
        position: { x, y },
    } = config;

    const { t } = useTranslation();
    const translate = (key, options) => t(`experience_calendar.schedule_form.${key}`, options);
    const popoverRef = useRef();
    const [activeTab, setActiveTab] = useState(0);

    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

    const formInitialValues = useMemo(() => {
        const initialDateValues = {
            start: null,
            end: null,
            startDuration: null,
            endDuration: null,
            timezone: initialValues?.timezone || timezone || null,
        };

        if (initialValues?.start?.isValid && initialValues?.end?.isValid) {
            initialDateValues.start = initialValues.start;
            initialDateValues.end = initialValues.end;
            initialDateValues.startDuration = initialValues?.startDuration || null;
            initialDateValues.endDuration = initialValues?.endDuration || null;
        } else if (start && end) {
            const newStartDate = DateTime.fromJSDate(start, { zone: initialDateValues.timezone });
            const newEndDate = DateTime.fromJSDate(end, { zone: initialDateValues.timezone });

            initialDateValues.start = newStartDate;
            initialDateValues.startDuration = newStartDate;
            initialDateValues.end = newEndDate;
            initialDateValues.endDuration = newEndDate;
        }

        return {
            ...initialValues,
            ...initialDateValues,
            hasPriceGroup: true, // TODO: USE THIS VALUE FROM EXPERIENCE
        };
    }, [initialValues, start, end, timezone]);

    const validateForm = (values) => {
        const errors = {};

        // WHEN TAB VALIDATIONS
        if (!values?.start?.isValid) {
            errors.start = translate('errors.start_date_required');
        }

        if (!values?.startDuration?.isValid) {
            errors.startDuration = t('common_form.required');
        }

        if (!values?.endDuration?.isValid) {
            errors.endDuration = t('common_form.required');
        }

        if (values?.hasEndDate && !values?.end?.isValid) {
            errors.end = translate('errors.end_date_required');
        }

        if (!values?.timezone) {
            errors.timezone = translate('errors.timezone_required');
        }

        if (values?.start?.isValid && values?.startDuration?.isValid && values?.endDuration?.isValid) {
            const startDate = values.start.set({
                hour: values.startDuration.hour,
                minute: values.startDuration.minute,
                second: 0,
                millisecond: 0,
            });

            let endDate = values.start;
            if (values?.hasEndDate && values?.end?.isValid) {
                endDate = values.end;
            }

            endDate = endDate.set({
                hour: values.endDuration.hour,
                minute: values.endDuration.minute,
                second: 0,
                millisecond: 0,
            });

            if (endDate.toMillis() <= startDate.toMillis()) {
                errors.endDuration = translate('errors.invalid_date_rante');
            }
        }

        // CAPACITY TAB VALIDATIONS
        if (Number.isInteger(parseInt(values?.capacity)) && !validateUnitCapacity(parseInt(values?.capacity), values?.units)) {
            errors.unitCapacity = translate('errors.invalid_unit_capacity');
        }

        if (!Number.isInteger(parseInt(values?.capacity)) && !values?.unlimited) {
            errors.capacity = translate('errors.invalid_capacity');
        }

        // PRICE TAB VALIDATIONS
        if (!Number.isFinite(parseFloat(values?.price)) && !values?.currency && !values?.priceGroupId) {
            errors.price = translate('errors.invalid_price');
        }

        // REPEAT TAB VALIDATIONS
        if (values?.repeat && values?.frequency === FREQUENCY.CUSTOM && (!Array.isArray(values?.selectedWeekDays) || values?.selectedWeekDays?.length <= 0)) {
            errors.selectedWeekDays = translate('errors.invalid_week_days');
        }

        if (values?.repeat && !values?.frequency) {
            errors.frequency = translate('errors.invalid_frequency');
        }

        if (values?.repeat && values?.intradayRepeat) {
            if (!values?.byHour && !values?.byMinute) {
                errors.intraday = translate('errors.invalid_intraday');
            }

            if (values?.byHour) {
                const byHour = values?.byHour;

                if (!byHour || byHour > 23 || byHour <= 0) {
                    errors.byHour = translate('errors.invalid_by_hour');
                }
            }

            if (values?.byMinute) {
                const byMinute = values?.byMinute;

                if (!byMinute || byMinute > 59 || byMinute <= 0) {
                    errors.byMinute = translate('errors.invalid_by_minute');
                }
            }
        }

        if (values?.repeat && values?.untilType && values?.untilType !== UNTIL_TYPE.INFINITE) {
            if (values.untilType === UNTIL_TYPE.COUNT && (!values?.untilCount || values?.untilCount <= 0)) {
                errors.untilCount = translate('errors.invalid_until_count');
            }

            if (values.untilType === UNTIL_TYPE.DATE) {
                let untilDate = values?.untilDate instanceof DateTime ? values.untilDate.startOf('day') : null;

                if (!untilDate) {
                    errors.untilDate = translate('errors.until_date_required');
                } else if (untilDate && values?.start?.isValid && values?.startDuration?.isValid) {
                    const startDate = values.start.startOf('day').plus({ days: 1 });

                    if (untilDate.toMillis() <= startDate.toMillis()) {
                        errors.untilDate = translate('errors.invalid_until_date');
                    }
                }
            }
        }

        return errors;
    };

    const handleSubmit = (values) => {
        let submitType = SUBMIT_TYPE.ADD;

        if (values?.isDelete && isEdit) {
            submitType = SUBMIT_TYPE.DELETE;
        } else if (isEdit) {
            submitType = SUBMIT_TYPE.UPDATE;
        }

        setActiveTab(0);
        return onSubmit(values, submitType);
    };

    const handleClose = () => {
        setActiveTab(0);
        return onClose();
    };

    if (!open) {
        return;
    }

    return (
        <Form
            initialValues={{ ...formInitialValues }}
            onSubmit={handleSubmit}
            validate={validateForm}
            mutators={{ ...arrayMutators }}
            render={({ handleSubmit, form }) => {
                const formContentProps = {
                    form: {
                        formApi: form,
                        handleSubmit,
                        handleClose,
                        isEdit,
                        loading,
                    },
                    ui: {
                        isMobile,
                        translate,
                        activeTab,
                        setActiveTab,
                    },
                };

                return (
                    <form key="experience-schedule-form">
                        {isMobile ? (
                            <BottomSheet
                                open={open}
                                title={isEdit ? translate('title.update') : translate('title.add')}
                                hasActions
                                actionBack={{
                                    label: translate('buttons.cancel'),
                                    onClick: (e) => {
                                        handleClose();
                                        return form.reset(e);
                                    },
                                }}
                                actionNext={{
                                    label: translate('buttons.add'),
                                    type: 'submit',
                                    onClick: (e) => {
                                        return handleSubmit(e);
                                    },
                                }}
                                handleToggle={() => {
                                    if (open) {
                                        handleClose();
                                    }
                                }}
                            >
                                <FormContent {...formContentProps} />
                            </BottomSheet>
                        ) : (
                            <Popover
                                ref={popoverRef}
                                onClose={handleClose}
                                open={open}
                                anchorReference="anchorPosition"
                                anchorPosition={{ top: y, left: x }}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'center',
                                    horizontal: 'center',
                                }}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            maxWidth: '680px',
                                            width: '100%',
                                        },
                                    },
                                }}
                                disableRestoreFocus
                                keepMounted
                            >
                                <FormContent {...formContentProps} />
                            </Popover>
                        )}
                    </form>
                );
            }}
        />
    );
};

export default ScheduleForm;
